import {useMutation} from '@apollo/client'
import {GoogleOAuthProvider, useGoogleLogin} from '@react-oauth/google'
import {useEffect, useRef, useState} from 'react'
import {Button, Card, Form, Modal, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {CREATE_INTEGRATION} from '../../gql/mutations/integrationsMutations'
import {IResCreateIntegration} from '../../types/integrationsTypes'
import {RING_CENTRAL_APP_SERVER, RING_CENTRAL_CLIENT_ID, GOOGLE_CLIENT_ID, STORAGE_NAMES, FEATURE_SHOW_GOOGLE_INTEGRATION} from '../../helpers/consts'
import {useLocation, useNavigate} from 'react-router-dom'
import {useDisclourse} from '../../hooks/useDisclourse'
import {ModalRingCentralConnect} from './components/ModalRingCentralConnect'
import {useTelephony, FALLBACK_INBOUND_GROUPS} from '../../hooks/useTelephony'
import {
  TELEPHONY_DRAWER_STATE_EVENT,
  TELEPHONY_DRAWER_STATE_REQUEST_EVENT,
  TelephonyDrawerStateDetail,
} from '../../types/telephony'

const INTEGRATIONS = [
  FEATURE_SHOW_GOOGLE_INTEGRATION
    ? {
        id: 1,
        title: 'Google',
        description:
          'Manage your emails efficiently, send, receive, and organize messages directly.',
        link: 'https://www.gmail.com/',
        urlImg: 'gmail.png',
      }
    : null,
  {
    id: 2,
    title: 'RingCentral',
    description:
      'Seamlessly manage calls and messages, collaborate with your team, and stay connected',
    link: 'https://www.ringcentral.com',
    urlImg: 'ringcentral.png',
  },
  {
    id: 3,
    title: 'WhatsApp',
    description: 'Manage WhatsApp templates and approvals directly.',
    link: '/integrations/whatsapp-templates',
    urlImg: 'botkmaker-1.png',
  },
  {
    id: 4,
    title: 'PolicyDial Webphone',
    description:
      'Open the PolicyDial agent session and webphone directly from PolicyTools for pilot users and production agents.',
    link: 'https://vici.policytools.com',
    urlImg: 'policydial-webphone.svg',
  },
].filter(Boolean) as Array<{
  id: number
  title: string
  description: string
  link: string
  urlImg: string
}>

const INTEGRATION_RING_ID = 'ring_init_id'

type SupervisorMonitorStage = 'MONITOR' | 'BARGE' | 'WHISPER'

interface SupervisorMonitorRequest {
  agentUser: string
  stage: SupervisorMonitorStage
  requestId: number
}

interface IntegrationsLocationState {
  supervisorMonitorRequest?: SupervisorMonitorRequest
}

interface TelephonySupervisorLocationState {
  pendingSupervisorMonitor?: SupervisorMonitorRequest
}

export const IntegrationsPage = () => {
  const [gqlCreateIntegration, {data, loading, error}] =
    useMutation<IResCreateIntegration>(CREATE_INTEGRATION)
  const location = useLocation()
  const navigate = useNavigate()
  const telephony = useTelephony()
  const handledSupervisorMonitorRequestRef = useRef(0)
  const telephonyResolved = telephony.resolved
  const vicidialEnabled = telephony.config.vicidial_enabled
  const vicidialAgentUser = telephony.config.vicidial_agent_user
  const activeVicidialSession = telephony.vicidialSession.activeSession
  const refreshVicidialSession = telephony.refreshVicidialSession
  const connectVicidialSession = telephony.connectVicidialSession
  const openVicidialWebphone = telephony.openWebphone
  const [showVicidialDetails, setShowVicidialDetails] = useState(false)
  const [showVicidialInboundModal, setShowVicidialInboundModal] = useState(false)
  const [connectingVicidialWithInbound, setConnectingVicidialWithInbound] = useState(false)
  const selectedVicidialCampaignOverrideId = telephony.vicidialSession.selectedCampaignId || ''
  const selectedVicidialCampaignId =
    selectedVicidialCampaignOverrideId || telephony.config.vicidial_campaign_id || ''
  const [draftVicidialCampaignId, setDraftVicidialCampaignId] = useState(
    selectedVicidialCampaignOverrideId
  )
  // Show ONLY the in-groups that belong to this agent: their PolicyTools-managed
  // group in-groups + their personal AGENTDIRECT queue (both supplied by the backend
  // in managed_group_ids). An agent with neither sees just the generic pool fallback
  // queue. Every other in-group (other groups, other campaigns' queues) is hidden.
  const managedInboundGroupIds = telephony.vicidialManagedGroupIds || []
  const hasManagedInbound = managedInboundGroupIds.length > 0
  const allowedInboundGroupIds = hasManagedInbound
    ? managedInboundGroupIds
    : FALLBACK_INBOUND_GROUPS
  const activeVicidialInboundGroups = telephony.vicidialInboundGroupOptions.filter(
    (group) =>
      (group.active ?? 'Y').toUpperCase() === 'Y' &&
      allowedInboundGroupIds.includes(group.group_id)
  )
  const availableVicidialInboundGroups = activeVicidialInboundGroups.filter(
    (group) => !telephony.selectedVicidialInboundGroups.includes(group.group_id)
  )
  const selectedVicidialInboundGroupOptions = activeVicidialInboundGroups.filter((group) =>
    telephony.selectedVicidialInboundGroups.includes(group.group_id)
  )
  // Only offer active campaigns (keep the currently-selected one even if it
  // went inactive, so the dropdown still reflects the live session).
  const vicidialSelectableCampaigns = telephony.vicidialCampaignOptions.filter(
    (c) => (c.active ?? 'Y').toUpperCase() === 'Y' || c.campaign_id === selectedVicidialCampaignId
  )
  const vicidialStatusLabel = telephony.vicidialSession.connecting
    ? 'Registering...'
    : telephony.vicidialSession.activeSession
      ? telephony.vicidialSession.status || 'Connected'
      : telephony.vicidialSession.connected
        ? 'Waiting for confirmation'
        : 'Disconnected'
  const vicidialCardMessage = telephony.vicidialSession.activeSession
    ? `Agent ${telephony.vicidialSession.agentUser || 'not configured'} is ready to use PolicyDial.`
    : telephony.vicidialSession.message || 'Use Details to review campaign and connection information.'

  if (!vicidialSelectableCampaigns.some((campaign) => campaign.campaign_id === '0001')) {
    vicidialSelectableCampaigns.unshift({
      campaign_id: '0001',
      campaign_name: 'Manual Dial Campaign',
      active: 'Y',
    })
  }

  if (
    selectedVicidialCampaignId &&
    !vicidialSelectableCampaigns.some((campaign) => campaign.campaign_id === selectedVicidialCampaignId)
  ) {
    vicidialSelectableCampaigns.unshift({
      campaign_id: selectedVicidialCampaignId,
      campaign_name: 'Selected campaign',
      active: 'Y',
    })
  }

  useEffect(() => {
    if (!showVicidialDetails) return
    setDraftVicidialCampaignId(selectedVicidialCampaignOverrideId)
  }, [selectedVicidialCampaignOverrideId, showVicidialDetails])

  const effectiveDraftVicidialCampaignId =
    draftVicidialCampaignId || telephony.config.vicidial_campaign_id || ''
  const hasVicidialCampaignChanges =
    draftVicidialCampaignId !== selectedVicidialCampaignOverrideId

  const [isLoading, setIsLoading] = useState(false)
  const [isConnectRing, setIsConnectRing] = useState(
    Boolean(localStorage.getItem(STORAGE_NAMES.RING_CENTRAL))
  )
  const login = useGoogleLogin({
    scope: 'https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/calendar.readonly',
    onSuccess: async (tokenResponse) => {
      const {access_token} = tokenResponse
      console.log('tokenResponse', tokenResponse)
      setIsLoading(true)
      try {
        const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: {Authorization: `Bearer ${access_token}`},
        })
        const resData = (await res.json()) as {email: string}
        gqlCreateIntegration({
          variables: {
            fromEmail: resData.email,
            integrationId: 1,
            token: access_token,
          },
        })
        localStorage.setItem(STORAGE_NAMES.TOKEN_GOOGLE, access_token)
        localStorage.setItem(STORAGE_NAMES.EMAIL_GOOGLE, resData.email)
      } catch (error) {
        toast.error('Error performing integration')
      } finally {
        setIsLoading(false)
      }
    },
    onError: (e) => {
      toast.error(`Error: ${e.error}`)
    },
  })

  const {
    isOpen: isOpenRingCentral,
    onClose: onCloseRingCentral,
    // onOpen: onOpenRingCentral,
  } = useDisclourse()

  const [isConnectedGoogle, setIsConnectedGoogle] = useState(
    Boolean(localStorage.getItem(STORAGE_NAMES.TOKEN_GOOGLE))
  )

  const handleDisconnectGoogle = () => {
    localStorage.removeItem(STORAGE_NAMES.TOKEN_GOOGLE)
    localStorage.removeItem(STORAGE_NAMES.EMAIL_GOOGLE)
    setIsConnectedGoogle(false)
    toast.info('Google disconnected')
  }

  useEffect(() => {
    if (error) {
      toast.error(`Error: ${error}`)
      setIsLoading(false)
      localStorage.removeItem(STORAGE_NAMES.TOKEN_GOOGLE)
      localStorage.removeItem(STORAGE_NAMES.EMAIL_GOOGLE)
    }
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.createUserIntegration?.userIntegration?.id) {
      toast.success(`Google connected successfully`)
      //navigate('/inbox')
      setIsConnectedGoogle(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (!telephonyResolved || !vicidialEnabled || !vicidialAgentUser) {
      return
    }

    refreshVicidialSession({silent: true})
  }, [refreshVicidialSession, telephonyResolved, vicidialAgentUser, vicidialEnabled])

  useEffect(() => {
    const supervisorMonitorRequest = (location.state as IntegrationsLocationState | null)
      ?.supervisorMonitorRequest
    if (!telephonyResolved || !supervisorMonitorRequest?.requestId) return
    if (handledSupervisorMonitorRequestRef.current === supervisorMonitorRequest.requestId) return

    handledSupervisorMonitorRequestRef.current = supervisorMonitorRequest.requestId

    const queryDrawerLive = (timeoutMs = 250) =>
      new Promise<boolean>((resolve) => {
        let settled = false
        const handle = (event: Event) => {
          if (settled) return
          settled = true
          window.removeEventListener(TELEPHONY_DRAWER_STATE_EVENT, handle as EventListener)
          const detail = (event as CustomEvent<TelephonyDrawerStateDetail>).detail
          resolve(Boolean(detail?.mounted && detail?.provider === 'VICIDIAL'))
        }
        window.addEventListener(TELEPHONY_DRAWER_STATE_EVENT, handle as EventListener)
        window.dispatchEvent(new CustomEvent(TELEPHONY_DRAWER_STATE_REQUEST_EVENT))
        window.setTimeout(() => {
          if (settled) return
          settled = true
          window.removeEventListener(TELEPHONY_DRAWER_STATE_EVENT, handle as EventListener)
          resolve(false)
        }, timeoutMs)
      })

    const runSupervisorConnectFlow = async () => {
      if (activeVicidialSession) {
        const drawerLive = await queryDrawerLive()
        if (!drawerLive) {
          const webphoneResponse = await openVicidialWebphone('VICIDIAL', {
            preferAgentSession: true,
            allowExternalFallback: false,
          })
          if (!webphoneResponse) return
        }
      } else {
        const sessionStatus = await connectVicidialSession()
        if (!sessionStatus) return
      }

      navigate('/telephony-supervisor', {
        replace: true,
        state: {
          pendingSupervisorMonitor: supervisorMonitorRequest,
        } as TelephonySupervisorLocationState,
      })
    }

    void runSupervisorConnectFlow()
  }, [
    activeVicidialSession,
    connectVicidialSession,
    location.state,
    navigate,
    openVicidialWebphone,
    telephonyResolved,
  ])

  const INBOUND_CAPABLE_CAMPAIGN_ID = '0001'
  const isInboundCapableCampaign = (campaignId: string) =>
    !campaignId || campaignId === INBOUND_CAPABLE_CAMPAIGN_ID

  const openVicidialInboundModal = async () => {
    if (!isInboundCapableCampaign(selectedVicidialCampaignId)) {
      setConnectingVicidialWithInbound(true)
      try {
        await telephony.connectVicidialSession()
      } finally {
        setConnectingVicidialWithInbound(false)
      }
      return
    }

    await telephony.loadVicidialInboundGroups()
    setShowVicidialInboundModal(true)
  }

  const toggleVicidialInboundGroup = (groupId: string, selected: boolean) => {
    if (!groupId) return

    if (selected) {
      telephony.setVicidialInboundGroups(
        telephony.selectedVicidialInboundGroups.filter((value) => value !== groupId)
      )
      return
    }

    telephony.setVicidialInboundGroups([
      ...telephony.selectedVicidialInboundGroups,
      groupId,
    ])
  }

  const handleVicidialConnectWithInboundGroups = async () => {
    setConnectingVicidialWithInbound(true)
    try {
      const sessionStatus = await telephony.connectVicidialSession()
      if (!sessionStatus) return

      setShowVicidialInboundModal(false)
      const ingroupsApplied = await telephony.applyVicidialInboundGroups()
      if (ingroupsApplied) {
        await telephony.setVicidialPause(false)
      }
    } finally {
      setConnectingVicidialWithInbound(false)
    }
  }

  const handleSaveVicidialCampaign = () => {
    telephony.setVicidialCampaign(draftVicidialCampaignId)

    if (draftVicidialCampaignId) {
      toast.success(`PolicyDial campaign ${draftVicidialCampaignId} saved for this session.`)
    } else if (effectiveDraftVicidialCampaignId) {
      toast.success(
        `PolicyDial will use the organization default campaign ${effectiveDraftVicidialCampaignId}.`
      )
    } else {
      toast.success('PolicyDial session campaign cleared.')
    }

    if (
      telephony.vicidialSession.activeSession &&
      telephony.vicidialSession.campaignId &&
      telephony.vicidialSession.campaignId !== effectiveDraftVicidialCampaignId
    ) {
      toast.info('Reconnect PolicyDial to apply the new campaign.')
    }

    setShowVicidialDetails(false)
  }

  /* useEffect(() => {
    const loadScript = () => {
      const nWindow = window as unknown as IWindow

      if (nWindow.RingCentralC2D) {
        const clickToDial = new nWindow.RingCentralC2D()
        clickToDial.on(nWindow.RingCentralC2D.events.call, (phoneNumber) => {
          nWindow.RCAdapter.clickToCall(phoneNumber, true)
        })
        clickToDial.on(nWindow.RingCentralC2D.events.text, (phoneNumber) => {
          nWindow.RCAdapter.clickToSMS(phoneNumber)
        })
        console.log('RingCentralC2D cargado:', clickToDial)
      } else {
        console.error('RingCentralC2D no está disponible')
      }
    }

    window.addEventListener('focus', loadScript)
    window.addEventListener('message', (e) => {
      const data = e.data
      console.log('data', data)
    })

    return () => {
      window.removeEventListener('focus', loadScript)
    }
  }, []) */

  const handleDisconnect = () => {
    /* document.getElementById(INTEGRATION_RING_ID)?.remove()
    document.getElementById(STORAGE_NAMES.RING_CENTRAL)?.remove()
    document.querySelector('rc-c2d-menu')?.remove()
    document.querySelector('#rc-widget')?.remove()
    document.getElementById('ring_init_id')?.remove() */
    localStorage.removeItem(STORAGE_NAMES.RING_CENTRAL)
    setIsConnectRing(false)
    window.location.reload()
  }

  const handleClick = () => {
    const isRingIntegration = localStorage.getItem(STORAGE_NAMES.RING_CENTRAL)
    if (isRingIntegration) return /* eslint-disable */
    ;(function () {
      var rcs = document.createElement('script')

      rcs.src = `https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/adapter.js?clientId=${RING_CENTRAL_CLIENT_ID}&appServer=${RING_CENTRAL_APP_SERVER}`
      rcs.id = INTEGRATION_RING_ID
      var rcs0 = document.getElementsByTagName('script')[0]
      rcs0?.parentNode?.insertBefore(rcs, rcs0)
    })()
    const script2 = document.createElement('script')
    script2.src = 'https://unpkg.com/ringcentral-c2d@1.0.0/build/index.js'
    script2.id = STORAGE_NAMES.RING_CENTRAL

    // Agregar el script al body
    document.body.appendChild(script2)

    // Confirmar que el script fue cargado
    script2.onload = () => {
      var clickToDial = new (window as any).RingCentralC2D()
      clickToDial.on((window as any).RingCentralC2D.events.call, (phoneNumber) => {
        ;(window as any).RCAdapter.clickToCall(phoneNumber, true)
      })
      clickToDial.on((window as any).RingCentralC2D.events.text, (phoneNumber) => {
        ;(window as any).RCAdapter.clickToSMS(phoneNumber)
      })
    }

    script2.onerror = () => {
      console.log('Error al cargar el script.')
    }
    localStorage.setItem(STORAGE_NAMES.RING_CENTRAL, STORAGE_NAMES.RING_CENTRAL)
    setIsConnectRing(true)
    /* ;(function () {
      var rcs = document.createElement('script')
      rcs.src = 'https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/adapter.js'
      var rcs0 = document.getElementsByTagName('script')[0]
      rcs0?.parentNode?.insertBefore(rcs, rcs0)
    })() */
    // Interact with RingCentral C2D
  }

  return (
    <>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <div>
          <h3>Integrations</h3>
          <span>Enhance Workflows with Advanced Integrations.</span>
          <div
            className='d-grid gap-4 mt-5'
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))',
            }}
          >
            {INTEGRATIONS.map((info) => (
              <Card key={info.id} className='h-100 d-flex flex-column' style={{width: '100%'}}>
                <Card.Body className='d-flex flex-column'>
                  <div className='d-flex align-items-center justify-content-between'>
                    <img
                      src={`/media/tfi/${info.urlImg}`}
                      alt={`Integration - ${info.title}`}
                      style={{
                        maxWidth:
                          info.title === 'RingCentral'
                            ? '80px'
                            : info.title === 'PolicyDial Webphone'
                              ? '120px'
                              : '4rem',
                        minHeight: '60px',
                      }}
                    />
                    <Card.Link href={info.link} target='_blank' rel='noopener noreferrer'>
                      <i className='bi bi-box-arrow-up-right' style={{color: 'inherit'}} />
                    </Card.Link>
                  </div>
                  <Card.Title>{info.title}</Card.Title>
                  <Card.Text className='flex-grow-1'>{info.description}</Card.Text>
                  {info.title === 'PolicyDial Webphone' && (
                    <div className='text-muted fs-8 mt-3'>
                      <div>
                        <strong>Status:</strong> {vicidialStatusLabel}
                      </div>
                      <div className='mt-1'>{vicidialCardMessage}</div>
                    </div>
                  )}
                </Card.Body>
                <Card.Footer>
                  <div className='d-flex align-items-center justify-content-between flex-wrap gap-2'>
                    {info.title === 'Google' ? (
                      isConnectedGoogle ? (
                        <Button
                          variant='outline-danger'
                          className='btn btn-transparent border d-flex align-items-center gap-2'
                          onClick={handleDisconnectGoogle}
                        >
                          <i className='bi bi-power' style={{color: 'inherit'}} />
                          Disconnect
                        </Button>
                      ) : (
                        <Button
                          variant='outline'
                          className='btn btn-transparent border d-flex align-items-center gap-2'
                          onClick={() => login()}
                        >
                          <i className='bi bi-file-play' style={{color: 'inherit'}} />
                          Connect
                        </Button>
                      )
                    ) : info.title === 'RingCentral' ? (
                      <>
                        {isConnectRing ? (
                          <Button
                            variant='outline'
                            className='btn btn-transparent border d-flex align-items-center gap-2 text-danger'
                            onClick={handleDisconnect}
                          >
                            <i className='bi bi-power' style={{color: 'inherit'}} />
                            Disconnect
                          </Button>
                        ) : (
                          <Button
                            variant='outline'
                            className='btn btn-transparent border d-flex align-items-center gap-2'
                            /* onClick={() => {
                          const YOUR_RINGCENTRAL_CLIENT_ID = 'Z9YGOPXDJkVfVDDW7RM8ll'

                          const rcs = document.createElement('script')
                          // rcs.src = 'https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/adapter.js?clientId=Z9YGOPXDJkVfVDDW7RM8ll&appServer=https://platform.devtest.ringcentral.com&redirectUri=https://app.policytools.com/'

                          const src = `https://apps.ringcentral.com/integration/ringcentral-embeddable/2.0.0/adapter.js?clientId=${YOUR_RINGCENTRAL_CLIENT_ID}&appServer=https://platform.devtest.ringcentral.com&enablePopup=1&multipleTabsSupport=1&redirectUri=${REDIRECT_URL}`
                          console.log('src', src)
                          rcs.src = src
                          const rcs0 = document.getElementsByTagName('script')[0]
                          rcs0?.parentNode?.insertBefore(rcs, rcs0)
                          rcs0.onload = () => {
                            ;(window as unknown as any).RCAdapterInit()
                          }

                          // onOpenRingCentral()
                        }} */
                            onClick={handleClick}
                            // onClick={onOpenRingCentral}
                          >
                            <i className='bi bi-file-play' style={{color: 'inherit'}} />
                            Connect
                          </Button>
                        )}
                      </>
                    ) : info.title === 'WhatsApp' ? (
                      <Button
                        variant='outline'
                        className='btn btn-transparent border d-flex align-items-center gap-2'
                        onClick={() => navigate('/integrations/whatsapp-templates')}
                      >
                        <i className='bi bi-file-earmark-text' style={{color: 'inherit'}} />
                        View templates
                      </Button>
                    ) : info.title === 'PolicyDial Webphone' ? (
                      <div className='d-flex align-items-center gap-2 flex-wrap'>
                        <Button
                          variant='outline-secondary'
                          className='btn btn-transparent border d-flex align-items-center gap-2'
                          onClick={() => setShowVicidialDetails(true)}
                        >
                          <i className='bi bi-info-circle' style={{color: 'inherit'}} />
                          Details
                        </Button>
                        {telephony.config.vicidial_supervisor_access && (
                          <Button
                            variant='outline-warning'
                            className='btn btn-transparent border d-flex align-items-center gap-2'
                            onClick={() => navigate('/telephony-supervisor')}
                          >
                            <i className='bi bi-speedometer2' style={{color: 'inherit'}} />
                            Supervisor
                          </Button>
                        )}
                        {telephony.vicidialSession.connecting ||
                        telephony.vicidialSession.connected ||
                        telephony.vicidialSession.activeSession ? (
                          <>
                            <Button
                              variant='outline'
                              className='btn btn-transparent border d-flex align-items-center gap-2'
                              onClick={() =>
                                telephony.openWebphone('VICIDIAL', {
                                  preferAgentSession: true,
                                })
                              }
                            >
                              <i className='bi bi-headset' style={{color: 'inherit'}} />
                              Open webphone
                            </Button>
                            <Button
                              variant='outline'
                              className='btn btn-transparent border d-flex align-items-center gap-2'
                              onClick={() => telephony.refreshVicidialSession()}
                              disabled={telephony.vicidialSession.connecting}
                            >
                              <i className='bi bi-arrow-clockwise' style={{color: 'inherit'}} />
                              Refresh
                            </Button>
                            <Button
                              variant='outline'
                              className='btn btn-transparent border d-flex align-items-center gap-2 text-danger'
                              onClick={telephony.disconnectVicidialSession}
                            >
                              <i className='bi bi-power' style={{color: 'inherit'}} />
                              Disconnect
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant='outline'
                            className='btn btn-transparent border d-flex align-items-center gap-2'
                            onClick={openVicidialInboundModal}
                            disabled={
                              telephony.vicidialSession.connecting ||
                              !telephony.config.vicidial_enabled ||
                              !telephony.config.vicidial_agent_user
                            }
                          >
                            {telephony.vicidialSession.connecting ? (
                              <Spinner animation='border' size='sm' />
                            ) : (
                              <i className='bi bi-headset' style={{color: 'inherit'}} />
                            )}
                            {telephony.vicidialSession.connecting ? 'Connecting...' : 'Connect'}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Card.Link
                        className='btn btn-transparent border d-flex align-items-center gap-2'
                        href={info.link}
                      >
                        <i className='bi bi-file-play' style={{color: 'inherit'}} />
                        Connect
                      </Card.Link>
                    )}
                  </div>
                </Card.Footer>
              </Card>
            ))}
          </div>
        </div>
      </GoogleOAuthProvider>
      {(isLoading || loading) && (
        <div
          className='d-flex flex-center'
          style={{
            inset: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#000',
            color: 'white',
            opacity: 0.5,
            position: 'absolute',
            zIndex: 10000000,
          }}
        >
          <div>
            <Spinner animation='border' style={{height: '3.5em', width: '3.5em'}} />
          </div>
        </div>
      )}

      {isOpenRingCentral && (
        <ModalRingCentralConnect isOpen={isOpenRingCentral} onClose={onCloseRingCentral} />
      )}
      <Modal show={showVicidialDetails} onHide={() => setShowVicidialDetails(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>PolicyDial Connection Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='border rounded p-3 mb-3 bg-light'>
            <div className='fw-semibold text-dark mb-2'>Campaign for this session</div>
            <div className='d-flex align-items-end gap-2 flex-wrap'>
              <Form.Group className='flex-grow-1 mb-0' style={{minWidth: '220px'}}>
                <Form.Select
                  size='sm'
                  value={draftVicidialCampaignId}
                  onChange={(e) => setDraftVicidialCampaignId(e.target.value)}
                >
                  <option value=''>
                    Use organization default
                    {telephony.config.vicidial_campaign_id
                      ? ` (${telephony.config.vicidial_campaign_id})`
                      : ''}
                  </option>
                  {vicidialSelectableCampaigns.map((campaign) => (
                    <option key={campaign.campaign_id} value={campaign.campaign_id}>
                      {campaign.campaign_id} - {campaign.campaign_name}
                      {campaign.active === 'Y' ? '' : ' (Inactive)'}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Button
                variant='light-primary'
                size='sm'
                onClick={() => telephony.loadVicidialCampaigns()}
                disabled={telephony.loadingVicidialCampaigns}
              >
                {telephony.loadingVicidialCampaigns ? 'Loading...' : 'Load campaigns'}
              </Button>
              <Button
                variant='primary'
                size='sm'
                onClick={handleSaveVicidialCampaign}
                disabled={!hasVicidialCampaignChanges}
              >
                <i className='bi bi-check-lg me-1' />
                Save
              </Button>
            </div>
            <div className='mt-2'>
              Keep <code>0001</code> available when you need manual dialing.
            </div>
          </div>
          <div className='text-muted fs-7'>
            <div>
              <strong>Agent:</strong> {telephony.vicidialSession.agentUser || 'Not configured'}
            </div>
            <div>
              <strong>Phone:</strong> {telephony.vicidialSession.phoneLogin || 'Not configured'}
            </div>
            <div>
              <strong>Status:</strong> {vicidialStatusLabel}
            </div>
            {telephony.vicidialSession.campaignId &&
            telephony.vicidialSession.campaignId !== effectiveDraftVicidialCampaignId ? (
              <div>
                <strong>Active campaign:</strong> {telephony.vicidialSession.campaignId}
              </div>
            ) : null}
            {telephony.vicidialSession.pauseCode && (
              <div>
                <strong>Pause code:</strong> {telephony.vicidialSession.pauseCode}
              </div>
            )}
            {telephony.vicidialSession.sessionId && (
              <div>
                <strong>Session:</strong> {telephony.vicidialSession.sessionId}
              </div>
            )}
            <div className='mt-2'>{telephony.vicidialSession.message}</div>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={showVicidialInboundModal}
        onHide={() => !connectingVicidialWithInbound && setShowVicidialInboundModal(false)}
        centered
        size='lg'
      >
        <Modal.Header closeButton={!connectingVicidialWithInbound}>
          <Modal.Title>CLOSER INBOUND GROUP SELECTION</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='text-muted fs-7 mb-3'>
            Select the inbound groups that should receive calls for this PolicyDial session.
          </div>
          <div className='row g-3'>
            <div className='col-md-6'>
              <div className='fw-bold text-uppercase text-center mb-2'>Groups Not Selected</div>
              <div className='border rounded p-2 bg-light' style={{minHeight: '280px', maxHeight: '320px', overflowY: 'auto'}}>
                <div
                  role='button'
                  className='text-center fw-semibold text-primary mb-2'
                  onClick={() =>
                    telephony.setVicidialInboundGroups(
                      activeVicidialInboundGroups.map((group) => group.group_id)
                    )
                  }
                >
                  --- ADD ALL ---
                </div>
                {availableVicidialInboundGroups.length ? (
                  availableVicidialInboundGroups.map((group) => (
                    <div
                      key={group.group_id}
                      role='button'
                      className='py-1 text-primary text-decoration-underline'
                      onClick={() => toggleVicidialInboundGroup(group.group_id, false)}
                    >
                      {group.group_name}
                      {group.group_name !== group.group_id && (
                        <span className='text-muted small ms-2'>({group.group_id})</span>
                      )}
                      {(telephony.vicidialManagedGroupNumbers?.[group.group_id] || []).length >
                        0 && (
                        <span className='text-success small ms-2 fw-semibold'>
                          {(telephony.vicidialManagedGroupNumbers?.[group.group_id] || []).join(
                            ', '
                          )}
                        </span>
                      )}
                    </div>
                  ))
                ) : (
                  <div className='text-muted small'>No inbound groups available.</div>
                )}
              </div>
            </div>
            <div className='col-md-6'>
              <div className='fw-bold text-uppercase text-center mb-2'>Selected Groups</div>
              <div className='border rounded p-2 bg-light' style={{minHeight: '280px', maxHeight: '320px', overflowY: 'auto'}}>
                <div
                  role='button'
                  className='text-center fw-semibold text-primary mb-2'
                  onClick={() => telephony.setVicidialInboundGroups([])}
                >
                  --- DELETE ALL ---
                </div>
                {selectedVicidialInboundGroupOptions.length ? (
                  selectedVicidialInboundGroupOptions.map((group) => (
                    <div
                      key={group.group_id}
                      role='button'
                      className='py-1 text-primary text-decoration-underline'
                      onClick={() => toggleVicidialInboundGroup(group.group_id, true)}
                    >
                      {group.group_name}
                      {group.group_name !== group.group_id && (
                        <span className='text-muted small ms-2'>({group.group_id})</span>
                      )}
                      {(telephony.vicidialManagedGroupNumbers?.[group.group_id] || []).length >
                        0 && (
                        <span className='text-success small ms-2 fw-semibold'>
                          {(telephony.vicidialManagedGroupNumbers?.[group.group_id] || []).join(
                            ', '
                          )}
                        </span>
                      )}
                    </div>
                  ))
                ) : (
                  <div className='text-muted small'>Select at least one inbound group.</div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='light'
            onClick={() => setShowVicidialInboundModal(false)}
            disabled={connectingVicidialWithInbound}
          >
            Cancel
          </Button>
          <Button
            variant='light-primary'
            onClick={() => telephony.loadVicidialInboundGroups()}
            disabled={telephony.loadingVicidialInboundGroups || connectingVicidialWithInbound}
          >
            {telephony.loadingVicidialInboundGroups ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant='primary'
            onClick={handleVicidialConnectWithInboundGroups}
            disabled={
              connectingVicidialWithInbound ||
              !telephony.selectedVicidialInboundGroups.length
            }
          >
            {connectingVicidialWithInbound ? 'Connecting...' : 'Connect'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
